import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Button,
  Alert,
  Typography,
  Snackbar
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Progress from "../../../components/progress/Progress";
import { routes } from "../../../routes";
import {
  FormWrapper,
  EntryWrapper,
} from "../../../components/customStyles/form";
import IFloor, { initialFloorState } from "../../../models/floor";
import FloorService from "../../../services/floor/FloorService";
import DataTable, { IColumn } from "../../../components/table/data/DataTable";
import localService from "../../../services/local/LocalService";
import ILocal from "../../../models/local";
import FloorFormFields from "./FloorFormFields";
import UserService from "../../../services/user/UserService";
import { ERole } from "../../../models/enums/role";
import IUser from "../../../models/user";
import buildingService from "../../../services/building/BuildingService";
import IAdminPageBuildingDetail from "../../../models/adminPage/adminPageBuildingDetail";
import { convertToSVG } from "../../../utils/convertToSVG";
import EditOnly from "../../../components/permissions/EditOnly";

const useLocalColumns = () => {
  const { t } = useTranslation();

  const columns: IColumn<ILocal>[] = [
    {
      field: "name",
      headerName: t("table.local.name"),
    },
    {
      field: "profileName",
      headerName: t("local.profile"),
    },
    {
      field: "codes",
      headerName: t("local.codes"),
      transform: {
        render: (data) => (
          <Typography variant="body2">
            {data.codes.join(", ")}
          </Typography>
        ),
        filterTransform: (data) => {
          return data.codes.join(", ");
        },
      },
    },
  ];
  return columns;
};

const ROWS_PER_PAGE = 10;
const SUCCESS_STATUS_CODE = 200;

const FloorEditPage = () => {
  const { t } = useTranslation(["common", "admin"]);

  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state;

  const [isErrorMessageVisible, setIsErrorMessageVisible] = useState(false);

  const [floorState, setFloorState] =
    useState<IFloor>(initialFloorState);
  const [cachedFloor, setCachedFloor] =
    useState<IFloor>(initialFloorState);
  const [isFloorChanged, setIsFloorChanged] = useState(false);

  const [buildings, setBuildings] = useState<IAdminPageBuildingDetail[]>([]);

  const [superAdminsIds, setSuperAdminsIds] = useState<number[]>([]);

  const [hbscanMapFile, setHbscanMapFile] = useState<File>();
  const [hbscanMapURL, setHbscanMapURL] = useState('');

  const [locals, setLocals] = useState<ILocal[]>([]);
  const localColumns = useLocalColumns();

  const [isFormSending, setIsFormSending] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const onSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const onUpdateFloor = (event: FormEvent) => {
    event.preventDefault();

    setIsFormSending(true);

    const floor = new FormData();

    floor.append('id', floorState.id.toString());
    floor.append('name', floorState.name);
    floor.append('buildingId', floorState.buildingId.toString());
    floor.append('floorNumber', floorState.floorNumber.toString());
    floor.append('isOvertimeCalculation', floorState.isOvertimeCalculation.toString());

    if (floorState.managerIds) {
      const managerIds = [...floorState.managerIds, ...superAdminsIds];

      managerIds.forEach(managerId => {
        floor.append('managerIds[]', managerId.toString());
      });
    }

    if (hbscanMapFile) floor.append('hbscanMap', hbscanMapFile);

    FloorService.PutFloor(floor)
      .then((response) => {
        if (response.status === SUCCESS_STATUS_CODE) {
          navigate(0);
          return;
        }
        setIsErrorMessageVisible(true);
      })
      .finally(() => setIsFormSending(false));
  };

  const createMapURL = (map: Blob | File) => {
    const url = URL.createObjectURL(map);
    setHbscanMapURL(url);
    return () => URL.revokeObjectURL(url);
  }

  useEffect(() => {
    buildingService.GetBuildingsDetails().then(setBuildings);

    FloorService.GetFloorById(id).then(async (floor) => {
      if (!floor) return;
      UserService.GetUsersByCompany().then((users) => {
        if (!users) return;
        const superAdmins = users.filter((user) => user.role === ERole.SuperAdmin).map((superAdmin) => superAdmin.id);

        setSuperAdminsIds(superAdmins);
        setFloorState(floor);
        setCachedFloor(floor);


      });

      if (!floor.hbscanMapUrl) return;

      fetch(floor.hbscanMapUrl)
        .then((response) => {
          return response.blob();
        })
        .then((blob) => {
          const fileName = floor.hbscanMapUrl.split('/').pop() ?? '';
          const svgBlob = new Blob([blob], { type: 'image/svg+xml' });
          setHbscanMapFile(new File([svgBlob], fileName));
          createMapURL(svgBlob);
        });

    });

    localService.GetLocalsByFloor(id).then((locals) => {
      if (!locals) return;
      setLocals(locals);
    });

  }, [id]);

  useEffect(() => {
    setIsFloorChanged(false);
    if (JSON.stringify(cachedFloor) != JSON.stringify(floorState))
      setIsFloorChanged(true);
  }, [floorState]);


  const onFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];

    if (!allowedTypes.includes(file.type)) {
      setSnackbarMessage(t("table.floor.onlyPictureAllowed"));
      setSnackbarOpen(true);
      e.target.value = '';
      return;
    }

    try {
      const svgFile = await convertToSVG(file);

      setHbscanMapFile(svgFile);
      createMapURL(svgFile);
      setIsFloorChanged(true);
    } catch (error) {

      setSnackbarMessage(t("table.floor.errorConvert"));
      setSnackbarOpen(true);
    }

    e.target.value = '';
  };

  const onRemoveFile = () => {
    setHbscanMapFile(undefined);
    setHbscanMapURL('');
    setIsFloorChanged(false);
    if (cachedFloor.hbscanMapUrl) setIsFloorChanged(true);
  }

  return (
    <Box>
      <Box component={Paper} sx={{ width: "100%" }}>
        <form autoComplete="off" onSubmit={onUpdateFloor}>
          <FormWrapper>
            {isErrorMessageVisible && (
              <Alert
                severity="error"
                sx={{ marginBottom: "1em" }}
              >
                {t("table.floor.editErrorMessage")}
              </Alert>
            )}

            <FloorFormFields
              floorState={floorState}
              setFloorState={setFloorState}
              hbscanMapURL={hbscanMapURL}
              onFileChange={onFileChange}
              onRemoveFile={onRemoveFile}
              buildings={buildings}
            />

            <EntryWrapper
              sx={{
                justifyContent: "flex-end",
                marginBlock: "1em",
              }}
            >

              <EntryWrapper
                sx={{ justifyContent: "flex-end" }}
              >
                <Progress isLoading={isFormSending}>
                  <EditOnly>
                    <Button
                      type="submit"
                      variant="contained"
                      color="secondary"
                      disabled={
                        !floorState.name.trim() ||
                        floorState.floorNumber === null ||
                        floorState.floorNumber === undefined ||
                        !floorState.buildingId ||
                        !isFloorChanged
                      }
                      sx={{
                        marginTop: "4em",
                      }}
                    >
                      {t("table.floor.edit")}
                    </Button>
                  </EditOnly>
                </Progress>
              </EntryWrapper>

            </EntryWrapper>
          </FormWrapper>
        </form>
      </Box>

      <Box component={Paper} sx={{ width: "100%", marginY: 4 }}>
        <Box sx={{ padding: 2, fontWeight: "bold" }}>
          {t("table.local.title")}
        </Box>
        <DataTable
          dense
          disableFiltrer={true}
          rows={locals}
          columns={localColumns}
          onRowClick={(row) =>
            navigate(routes.tableRoute.locals, {
              state: { ...row },
            })
          }
          rowsPerPageOptions={[ROWS_PER_PAGE]}
        />
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={onSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={onSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FloorEditPage;
